import { db, auth } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  doc, 
  updateDoc, 
  limit, 
  writeBatch,
  getDoc
} from 'firebase/firestore';
// Instead of using authService, let's adapt to use the current user setup
// For now, we'll use a simpler approach without user authentication

// Get the current user ID from Firebase Auth
const getUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated. Please sign in to manage collections.');
  }
  return user.uid;
};

// Get all collections for the current user
export const getCollections = async () => {
  const userId = getUserId();
  
  try {
    const collectionsRef = collection(db, 'collections');
    const collectionsQuery = query(
      collectionsRef,
      where('userId', '==', userId),
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(collectionsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting collections:', error);
    return [];
  }
};

// Create a new collection
export const createCollection = async (name, description = '') => {
  const userId = getUserId();
  
  try {
    const now = new Date();
    const collectionsRef = collection(db, 'collections');
    const docRef = await addDoc(collectionsRef, {
      name,
      description,
      userId,
      isDefault: false,
      createdAt: now,
      updatedAt: now
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

// Update a collection's metadata
export const updateCollection = async (collectionId, updates) => {
  try {
    const now = new Date();
    const collectionRef = doc(db, 'collections', collectionId);
    await updateDoc(collectionRef, {
      ...updates,
      updatedAt: now
    });
    return true;
  } catch (error) {
    console.error('Error updating collection:', error);
    throw error;
  }
};

// Delete a collection and all its items
export const deleteCollection = async (collectionId) => {
  try {
    // Get current user ID
    const userId = auth.currentUser.uid;
    
    const batch = writeBatch(db);
    
    // Delete the collection document
    const collectionRef = doc(db, 'collections', collectionId);
    batch.delete(collectionRef);
    
    // Find and delete all collection items
    const collectionItemsRef = collection(db, 'collection_items');
    const itemsQuery = query(
      collectionItemsRef,
      where('collectionId', '==', collectionId),
      where('userId', '==', userId)
    );
    const itemsSnapshot = await getDocs(itemsQuery);
      
    itemsSnapshot.docs.forEach(docSnapshot => {
      const itemRef = doc(db, 'collection_items', docSnapshot.id);
      batch.delete(itemRef);
    });
    
    // Commit the batch
    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error deleting collection:', error);
    throw error;
  }
};

// Add a thought to a collection
export const addThoughtToCollection = async (collectionId, thoughtId) => {
  try {
    // Get current user ID
    const userId = auth.currentUser.uid;
    
    // Check if item already exists
    const collectionItemsRef = collection(db, 'collection_items');
    const existingItemQuery = query(
      collectionItemsRef,
      where('collectionId', '==', collectionId),
      where('thoughtId', '==', thoughtId),
      limit(1)
    );
    const existingItem = await getDocs(existingItemQuery);
      
    if (!existingItem.empty) {
      console.log('Thought already in collection');
      return true;
    }
    
    // Get the highest position to add at the end
    const positionQuery = query(
      collectionItemsRef,
      where('collectionId', '==', collectionId),
      orderBy('position', 'desc'),
      limit(1)
    );
    const positionSnapshot = await getDocs(positionQuery);
      
    let position = 0;
    if (!positionSnapshot.empty) {
      position = positionSnapshot.docs[0].data().position + 1;
    }
    
    // Add the new item
    await addDoc(collectionItemsRef, {
      collectionId,
      thoughtId,
      userId, // Add user ID
      position,
      addedAt: new Date()
    });
    
    // Update the collection's updatedAt timestamp
    const collectionRef = doc(db, 'collections', collectionId);
    await updateDoc(collectionRef, {
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error('Error adding thought to collection:', error);
    throw error;
  }
};

// Add multiple thoughts to a collection in a batch
export const addThoughtsToCollectionBatch = async (collectionId, thoughtIds) => {
  if (!thoughtIds.length) return true;
  
  try {
    // Get current user ID
    const userId = auth.currentUser.uid;

    // Check which thoughts are already in the collection
    const collectionItemsRef = collection(db, 'collection_items');
    const existingItemsQuery = query(
      collectionItemsRef,
      where('collectionId', '==', collectionId),
      where('thoughtId', 'in', thoughtIds),
      where('userId', '==', userId)
    );
    const existingItems = await getDocs(existingItemsQuery);
    
    // Create a set of thought IDs that are already in the collection
    const existingThoughtIds = new Set();
    existingItems.forEach(doc => {
      existingThoughtIds.add(doc.data().thoughtId);
    });
    
    // Filter out thoughts that are already in the collection
    const thoughtsToAdd = thoughtIds.filter(id => !existingThoughtIds.has(id));
    
    if (thoughtsToAdd.length === 0) {
      console.log('All thoughts already in collection');
      return true;
    }
    
    // Create a batch
    const batch = writeBatch(db);
    const now = new Date();
    
    // Add each thought to the batch
    thoughtsToAdd.forEach((thoughtId) => {
      const newItemRef = doc(collectionItemsRef);
      batch.set(newItemRef, {
        collectionId,
        thoughtId,
        userId,
        position: 0, // Use a fixed position value
        addedAt: now
      });
    });
    
    // Update the collection's updatedAt timestamp
    const collectionRef = doc(db, 'collections', collectionId);
    batch.update(collectionRef, {
      updatedAt: now
    });
    
    // Commit the batch
    await batch.commit();
    
    return true;
  } catch (error) {
    console.error('Error adding thoughts to collection in batch:', error);
    throw error;
  }
};

// Remove a thought from a collection
export const removeThoughtFromCollection = async (collectionId, thoughtId) => {
  try {
    const collectionItemsRef = collection(db, 'collection_items');
    const itemsQuery = query(
      collectionItemsRef,
      where('collectionId', '==', collectionId),
      where('thoughtId', '==', thoughtId)
    );
    const itemsSnapshot = await getDocs(itemsQuery);
      
    if (itemsSnapshot.empty) {
      console.log('Thought not found in collection');
      return true;
    }
    
    // Delete the items
    const batch = writeBatch(db);
    itemsSnapshot.docs.forEach(docSnapshot => {
      const itemRef = doc(db, 'collection_items', docSnapshot.id);
      batch.delete(itemRef);
    });
    await batch.commit();
    
    // Update the collection's updatedAt timestamp
    const collectionRef = doc(db, 'collections', collectionId);
    await updateDoc(collectionRef, {
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error('Error removing thought from collection:', error);
    throw error;
  }
};

// Remove multiple thoughts from a collection in a batch
export const removeThoughtsFromCollectionBatch = async (collectionId, thoughtIds) => {
  if (!thoughtIds.length) return true;
  
  try {
    // Find the collection items to remove
    const collectionItemsRef = collection(db, 'collection_items');
    const itemsToRemoveQuery = query(
      collectionItemsRef,
      where('collectionId', '==', collectionId),
      where('thoughtId', 'in', thoughtIds)
    );
    const itemsToRemove = await getDocs(itemsToRemoveQuery);
    
    if (itemsToRemove.empty) {
      console.log('No thoughts to remove from collection');
      return true;
    }
    
    // Create a batch
    const batch = writeBatch(db);
    
    // Add each item to the batch for deletion
    itemsToRemove.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    // Update the collection's updatedAt timestamp
    const collectionRef = doc(db, 'collections', collectionId);
    batch.update(collectionRef, {
      updatedAt: new Date()
    });
    
    // Commit the batch
    await batch.commit();
    
    return true;
  } catch (error) {
    console.error('Error removing thoughts from collection in batch:', error);
    throw error;
  }
};

// Get all thoughts in a collection
export const getCollectionThoughts = async (collectionId) => {
  try {
    // First check if this is a tag collection
    const collectionRef = doc(db, 'collections', collectionId);
    const collectionDoc = await getDoc(collectionRef);
    
    if (!collectionDoc.exists()) {
      console.error(`Collection with ID ${collectionId} not found`);
      return [];
    }
    
    const collectionData = collectionDoc.data();
    
    // If this is a tag collection, use the tag-specific function
    if (collectionData.isTagCollection && collectionData.tagName) {
      return getThoughtsByTag(collectionData.tagName);
    }
    
    // Get current user ID
    const userId = auth.currentUser.uid;
    
    // Otherwise proceed with normal collection logic
    // Get the collection items sorted by position
    const collectionItemsRef = collection(db, 'collection_items');
    const itemsQuery = query(
      collectionItemsRef,
      where('collectionId', '==', collectionId),
      where('userId', '==', userId)
    );
    const itemsSnapshot = await getDocs(itemsQuery);
      
    if (itemsSnapshot.empty) {
      return [];
    }
    
    // Extract thought IDs
    const thoughtIds = itemsSnapshot.docs.map(doc => doc.data().thoughtId);
    
    // Instead of batch queries, fetch each thought individually with error handling
    const thoughts = [];
    
    // Fetch each thought - if one fails, we'll still get the others
    await Promise.all(thoughtIds.map(async (thoughtId) => {
      try {
        const thoughtDoc = await getDoc(doc(db, 'thoughts', thoughtId));
        
        if (thoughtDoc.exists()) {
          thoughts.push({
            id: thoughtDoc.id,
            ...thoughtDoc.data()
          });
        }
      } catch (error) {
        console.log(`Skipping thought ${thoughtId} due to access error`);
        // Just skip this thought - we'll return what we can access
      }
    }));
    
    // Return thoughts in original order as much as possible
    return thoughtIds
      .map(id => thoughts.find(t => t.id === id))
      .filter(Boolean); // Remove nulls (thoughts we couldn't access)
  } catch (error) {
    console.error('Error getting collection thoughts:', error);
    return [];
  }
};

// Get all thoughts with a specific tag
export const getThoughtsByTag = async (tagName) => {
  const userId = getUserId();
  
  try {
    // Query all thoughts that have this tag
    const thoughtsRef = collection(db, 'thoughts');
    const taggedThoughtsQuery = query(
      thoughtsRef,
      where('userId', '==', userId),
      where('tags', 'array-contains', tagName)
    );
    
    const snapshot = await getDocs(taggedThoughtsQuery);
    
    // Return the thoughts
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Error getting thoughts with tag ${tagName}:`, error);
    return [];
  }
};

// Ensure default collection exists
export const ensureDefaultCollection = async () => {
  const userId = getUserId();
  
  try {
    // Check if default collection exists
    const collectionsRef = collection(db, 'collections');
    const defaultCollectionQuery = query(
      collectionsRef,
      where('userId', '==', userId),
      where('isDefault', '==', true),
      limit(1)
    );
    
    const snapshot = await getDocs(defaultCollectionQuery);
    
    if (snapshot.empty) {
      // Create default collection
      const now = new Date();
      await addDoc(collectionsRef, {
        name: 'Your Thoughts',
        description: 'All your thoughts in one place',
        userId,
        isDefault: true,
        createdAt: now,
        updatedAt: now
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error ensuring default collection:', error);
    throw error;
  }
};

// Get all collections that contain a specific thought
export const getCollectionsForThought = async (thoughtId) => {
  try {
    // Get current user ID
    const userId = auth.currentUser.uid;
    
    // First get all collection items for this thought AND the current user
    const collectionItemsRef = collection(db, 'collection_items');
    const itemsQuery = query(
      collectionItemsRef,
      where('thoughtId', '==', thoughtId),
      where('userId', '==', userId) // Add userId filter
    );
    const itemsSnapshot = await getDocs(itemsQuery);
    
    if (itemsSnapshot.empty) {
      return [];
    }
    
    // Extract unique collection IDs
    const collectionIds = [...new Set(
      itemsSnapshot.docs.map(doc => doc.data().collectionId)
    )];
    
    // Batch query collections by ID
    const CHUNK_SIZE = 10; // Firestore limitation
    let collections = [];
    
    // Process collectionIds in chunks
    for (let i = 0; i < collectionIds.length; i += CHUNK_SIZE) {
      const chunk = collectionIds.slice(i, i + CHUNK_SIZE);
      
      // Create a query for this chunk of IDs
      const collectionsRef = collection(db, 'collections');
      // Add both ID and userId filters for security
      const batchQuery = query(
        collectionsRef, 
        where('__name__', 'in', chunk),
        where('userId', '==', userId) // Also filter collections by userId
      );
      const batchSnapshot = await getDocs(batchQuery);
      
      // Add all collections from this batch
      collections = [
        ...collections,
        ...batchSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      ];
    }
    
    // Sort by default first, then by name
    return collections.sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a.name.localeCompare(b.name);
    });
    
  } catch (error) {
    console.error('Error getting collections for thought:', error);
    return [];
  }
};

// Add a thought to multiple collections in a batch
export const addThoughtToCollectionsBatch = async (thoughtId, collectionIds) => {
  if (!collectionIds.length) return true;
  
  try {
    // Get current user ID
    const userId = auth.currentUser.uid;
    
    const batch = writeBatch(db);
    const now = new Date();
    const collectionItemsRef = collection(db, 'collection_items');

    // First check which collections already contain this thought
    const existingItemsQuery = query(
      collectionItemsRef,
      where('thoughtId', '==', thoughtId),
      where('collectionId', 'in', collectionIds),
      where('userId', '==', userId)
    );
    const existingItems = await getDocs(existingItemsQuery);
    
    // Track which collections already have this thought
    const existingCollectionIds = new Set();
    existingItems.forEach(doc => {
      existingCollectionIds.add(doc.data().collectionId);
    });
    
    // Filter out collections that already have this thought
    const collectionsToAdd = collectionIds.filter(id => !existingCollectionIds.has(id));
    
    if (collectionsToAdd.length === 0) {
      console.log('Thought already in all selected collections');
      return true;
    }
    
    // Now we can add all items to the batch
    collectionsToAdd.forEach((collectionId) => {
      const newItemRef = doc(collectionItemsRef);
      batch.set(newItemRef, {
        collectionId,
        thoughtId,
        userId,
        position: 0, // Just use 0 for all positions
        addedAt: now
      });
      
      // Update the collection's updatedAt timestamp
      const collectionRef = doc(db, 'collections', collectionId);
      batch.update(collectionRef, {
        updatedAt: now
      });
    });
    
    // Commit the batch
    await batch.commit();
    
    return true;
  } catch (error) {
    console.error('Error adding thought to collections in batch:', error);
    throw error;
  }
};

// Remove a thought from multiple collections in a batch
export const removeThoughtFromCollectionsBatch = async (thoughtId, collectionIds) => {
  if (!collectionIds.length) return true;
  
  try {
    // Find all collection items to remove in a single query
    const collectionItemsRef = collection(db, 'collection_items');
    const itemsToRemoveQuery = query(
      collectionItemsRef,
      where('thoughtId', '==', thoughtId),
      where('collectionId', 'in', collectionIds),
      where('userId', '==', getUserId())
    );
    const itemsToRemove = await getDocs(itemsToRemoveQuery);
    
    if (itemsToRemove.empty) {
      console.log('No collection items to remove');
      return true;
    }
    
    // Create a batch
    const batch = writeBatch(db);
    const now = new Date();
    
    // Track which collection IDs we actually removed items from
    const affectedCollectionIds = new Set();
    
    // Add each item to the batch for deletion
    itemsToRemove.forEach(doc => {
      affectedCollectionIds.add(doc.data().collectionId);
      batch.delete(doc.ref);
    });
    
    // Update the updatedAt timestamp for each affected collection
    affectedCollectionIds.forEach(collectionId => {
      const collectionRef = doc(db, 'collections', collectionId);
      batch.update(collectionRef, {
        updatedAt: now
      });
    });
    
    // Commit the batch
    await batch.commit();
    
    return true;
  } catch (error) {
    console.error('Error removing thought from collections in batch:', error);
    throw error;
  }
};

// Create or get a tag collection - returns collection ID
export const getOrCreateTagCollection = async (tagName) => {
  const userId = getUserId();
  
  try {
    // First check if this tag collection already exists
    const collectionsRef = collection(db, 'collections');
    const tagCollectionQuery = query(
      collectionsRef,
      where('userId', '==', userId),
      where('isTagCollection', '==', true),
      where('tagName', '==', tagName),
      limit(1)
    );
    
    const snapshot = await getDocs(tagCollectionQuery);
    
    // If the tag collection already exists, return its ID
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
      
    // Double-check to avoid race conditions
    // Do a second query just to be extra safe
    const doubleCheckSnapshot = await getDocs(tagCollectionQuery);
    if (!doubleCheckSnapshot.empty) {
      return doubleCheckSnapshot.docs[0].id;
    }
    
    // Otherwise create a new tag collection
    const now = new Date();
    const docRef = await addDoc(collectionsRef, {
      name: `/${tagName}`,
      description: `Thoughts tagged with /${tagName}`,
      userId,
      isDefault: false,
      isTagCollection: true,
      tagName: tagName,
      createdAt: now,
      updatedAt: now
    });
    
    console.log(`Created new tag collection for "${tagName}" with ID ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error(`Error creating tag collection for ${tagName}:`, error);
    throw error;
  }
};

// Get all tag collections
export const getTagCollections = async () => {
  const userId = getUserId();
  
  try {
    const collectionsRef = collection(db, 'collections');
    const tagCollectionsQuery = query(
      collectionsRef,
      where('userId', '==', userId),
      where('isTagCollection', '==', true),
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(tagCollectionsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting tag collections:', error);
    return [];
  }
};

// Automatically add a thought to all relevant tag collections
export const addThoughtToTagCollections = async (thought) => {
  if (!thought.tags || thought.tags.length === 0) return true;
  
  try {
    // Process each tag
    for (const tag of thought.tags) {
      // Get or create the tag collection
      const collectionId = await getOrCreateTagCollection(tag);
      
      // Add the thought to the collection
      await addThoughtToCollection(collectionId, thought.id);
    }
    
    return true;
  } catch (error) {
    console.error('Error adding thought to tag collections:', error);
    throw error;
  }
};
